import {
  Discussion,
  DiscussionAndMessages,
  DiscussionContext,
  DiscussionContextType,
  DiscussionCreate,
  DiscussionSummary,
} from "@cnx/common";

import i18n from "common/i18n";
import api from "services/api";

class DiscussionService {
  static fetchDiscussionById = async ({
    discussionId,
  }: {
    discussionId: string;
  }): Promise<DiscussionAndMessages> => {
    const { data } = await api.get<DiscussionAndMessages>(
      `/discussion?discussionId=${discussionId}`
    );
    return data;
  };

  static fetchDiscussionContextByDiscussionId = async ({
    discussionId,
  }: {
    discussionId: string;
  }): Promise<DiscussionContext> => {
    const { data } = await api.get<DiscussionContext>(
      `/discussion/context?discussionId=${discussionId}`
    );
    return data;
  };

  static fetchDiscussions = async (): Promise<DiscussionSummary[]> => {
    const { data } = await api.get<DiscussionSummary[]>(`/discussions`, {
      params: { translation: i18n.language },
    });
    return data;
  };

  static addDiscussion = async (body: DiscussionCreate) => {
    return api.post("/discussions", body);
  };

  static getEmailDiscussionsByContextId = async ({
    contextId,
  }: {
    contextId: string;
  }) => {
    const { data } = await api.get<Discussion[]>(`/email-discussions`, {
      params: {
        contextId,
      },
    });
    return data;
  };
  static getChatDiscussionContext = async ({
    context,
    contextId,
  }: {
    context: DiscussionContextType;
    contextId: string;
  }) => {
    const { data } = await api.get<DiscussionContext>(
      `/chat-discussions/context`,
      { params: { context, contextId } }
    );
    return data;
  };

  static createOrGetContextChatDiscussion = async ({
    contextId,
    context,
    discussionId,
  }: {
    contextId: string;
    context: DiscussionContextType;
    discussionId?: string | null;
  }) => {
    const { data } = await api.post<{
      id: string;
      context: DiscussionContextType;
      contextId: string;
    }>(`/chat-discussions`, undefined, {
      params: { contextId, context, discussionId },
      headers: { "Content-Type": "application/json" },
    });
    return data;
  };

  static createEmailDiscussion = async ({
    context,
    contextId,
    discussionCountryCode,
    discussion,
    attachments,
    location,
  }: {
    discussion: DiscussionCreate;
    contextId: string;
    context: DiscussionContextType;
    discussionCountryCode?: string;
    attachments: Blob[];
    location: { latitude?: string; longitude?: string };
  }) => {
    const formData = new FormData();

    const { latitude, longitude } = location;

    if (discussion.title) {
      formData.append("title", discussion.title);
    }
    if (discussion.message) {
      formData.append("message", discussion.message);
    }

    attachments.forEach(attachment => {
      formData.append("attachments", attachment);
    });

    if (discussion.countryCode) {
      formData.append("countryCode", discussion.countryCode);
    }

    if (latitude) {
      formData.append("latitude", latitude.toString());
    }

    if (longitude) {
      formData.append("longitude", longitude.toString());
    }

    const { data } = await api.post<Discussion>(
      `/email-discussions`,
      formData,
      {
        params: {
          contextId,
          context,
          discussionCountryCode,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return data;
  };
}

export default DiscussionService;
